import { Button, Field, Input, Switch, Textarea } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import ButtonGroup from "#root/components/ButtonGroup";
import { PERSON_ENUMS, TATTOO_LABEL, SCARS_LABEL } from "#root/utils/persons";
import { useCurrentUser } from "#root/queries/local";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useLocalDepartment, useLocalUnitId, useVTrackerSelectedIncident } from "#root/queries/local";
import { useInfiniteIncidents, useIncidentById, Incident } from "#root/queries/incidents";
import { useInfiniteLocations, useLocationById, Location } from "#root/queries/locations";
import { Person } from "#root/queries/persons";
import "./styles.scss";
import { useState } from "react";
import ImageUploader, {UploaderFile} from "#root/components/ImageUploader";
import { showToast } from "#root/components/GlobalToaster";
import { getUnitEnumLabelByUnit } from "#root/utils/enums";
import { usePreventNavigation } from "#root/services/offlineNavigationService";

export interface VictimFormData {
    unitId: string;
    department: string;
    incidentId: string;
    foundLocation: string;
    firstName: string;
    lastName: string;
    status: string;
    gender: number | undefined;
    age: number | undefined;
    weight: number | undefined;
    height: number | undefined;
    eyes: number | undefined;
    hair: number | undefined;
    features: number[] | undefined;
    additionalInfo: string;
    locationId: string;
    restrictions: boolean;
    personalImage?: UploaderFile;
    licenseImage?: UploaderFile;
    scarImages: UploaderFile[];
    tattooImages: UploaderFile[];
}

const initialVictimFormData: VictimFormData = {
    unitId: '',
    department: '',
    incidentId: '',
    foundLocation: '',
    firstName: '',
    lastName: '',
    status: '',
    gender: undefined,
    age: undefined,
    weight: undefined,
    height: undefined,
    eyes: undefined,
    hair: undefined,
    features: undefined,
    additionalInfo: '',
    locationId: '',
    restrictions: false,
    personalImage: undefined,
    licenseImage: undefined,
    scarImages: [],
    tattooImages: []
};

interface VictimFormProps {
    initialData?: Person;
    onSubmit: (victim: Omit<VictimFormData, 'id'>) => Promise<void>;
    submitButtonText: string;
    isSubmitting: boolean;
    onFileDelete?: (fileId: string) => void;
}

const VictimForm: React.FC<VictimFormProps> = ({
    initialData,
    onSubmit,
    submitButtonText,
    isSubmitting,
    onFileDelete
}) => {
    const navigate = useNavigate();

    // Use the navigation blocker
    usePreventNavigation(false);

    const { data: currentUser } = useCurrentUser();
    const unit = currentUser?.unit;
    const { unitId: localUnitId, setUnitId } = useLocalUnitId();
    const { department: localDepartment, setDepartment } = useLocalDepartment();
    const { selectedIncidentId: vtrackerIncidentId, setVTrackerSelectedIncidentId } = useVTrackerSelectedIncident();

    const [victimForm, setVictimForm] = useState<VictimFormData>(() => ({
        ...initialVictimFormData,
        ...(initialData || {}),
        unitId: initialData?.unitId || localUnitId || '',
        department: initialData?.department || localDepartment || '',
        incidentId: initialData?.incidentId || vtrackerIncidentId || ''
    }));

    const {
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentsQuery
    } = useInfiniteDropdown<Incident>({
        initialId: victimForm.incidentId || undefined,
        onChange: (id) => setVTrackerSelectedIncidentId(id),
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById
    });

    const {
        selectedData: selectedLocationData,
        handleChange: handleLocationChange,
        setFilter: setLocationFilter,
        queryResult: locationsQuery
    } = useInfiniteDropdown<Location>({
        initialId: victimForm.locationId || undefined,
        useInfiniteQuery: useInfiniteLocations,
        useItemById: useLocationById
    });

    const handleVictimFormChange = (updates: Partial<VictimFormData>) => {
        setVictimForm({ ...victimForm, ...updates });
        if (updates.unitId !== undefined && updates.unitId !== victimForm.unitId) {
            setUnitId(updates.unitId || null);
        }
        if (updates.department !== undefined && updates.department !== victimForm.department) {
            setDepartment(updates.department || null);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await onSubmit({
                ...victimForm,
                firstName: victimForm.firstName.trim(),
                lastName: victimForm.lastName.trim()
            });
        } catch (error) {
            // Error handling is done in the parent component
        }
    };

    return (
        <form autoComplete="off" onSubmit={handleFormSubmit} className={`victim-form${isSubmitting ? ' submitting' : ''}`}>
            <h3>Incident information <div className="divider"></div></h3>

            <Field label="Unit ID" required>
                <Input
                    value={victimForm.unitId}
                    onChange={(e) => handleVictimFormChange({ unitId: e.target.value })}
                />
            </Field>

            <Field label="Department" required>
                <Input
                    value={victimForm.department}
                    onChange={(e) => handleVictimFormChange({ department: e.target.value })}
                />
            </Field>

            <Field label="Incident" required>
                <InfiniteDropdown
                    value={victimForm.incidentId}
                    onChange={(_, data) => {
                        handleIncidentChange(data.value);
                        handleVictimFormChange({ incidentId: data.value });
                    }}
                    onFilterChange={setIncidentFilter}
                    queryResult={incidentsQuery}
                    itemToOption={(incident) => ({
                        value: incident.id ?? '',
                        text: incident.name ?? ''
                    })}
                    placeholder="Select incident"
                    selectedItem={selectedIncidentData}
                />
            </Field>

            <Field label="Incident Address">
                <Input
                    value={selectedIncidentData?.address || ''}
                    readOnly
                />
            </Field>

            <h3>Victim information <div className="divider"></div></h3>

            <Field label="Was Found In Location">
                <Input
                    value={victimForm.foundLocation}
                    onChange={(e) => handleVictimFormChange({ foundLocation: e.target.value })}
                />
            </Field>

            <div className="field-two-columns">
                <Field label="First Name" required>
                    <Input
                        value={victimForm.firstName}
                        onChange={(e) => handleVictimFormChange({ firstName: e.target.value })}
                    />
                </Field>
                <Field label="Last Name" required>
                    <Input
                        value={victimForm.lastName}
                        onChange={(e) => handleVictimFormChange({ lastName: e.target.value })}
                    />
                </Field>
            </div>

            <Field label="Status" required>
                <ButtonGroup
                    options={PERSON_ENUMS.STATUS_OPTIONS.map(e => ({ label: e, value: e }))}
                    value={victimForm.status}
                    onChange={(value) => handleVictimFormChange({ status: value })}
                    name="status"
                    required
                />
            </Field>

            <Field label="Gender" required>
                <ButtonGroup
                    options={PERSON_ENUMS.GENDER.map(e => ({ label: e.label, value: e.value }))}
                    value={victimForm.gender}
                    onChange={(value) => handleVictimFormChange({ gender: value })}
                    name="gender"
                    required
                />
            </Field>

            <Field label="Estimated Age" required>
                <ButtonGroup
                    options={PERSON_ENUMS.AGE_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={victimForm.age}
                    onChange={(value) => handleVictimFormChange({ age: value })}
                    name="ageRange"
                    required
                />
            </Field>

            <Field label="Approximate Weight" required>
                <ButtonGroup
                    options={PERSON_ENUMS.WEIGHT_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={victimForm.weight}
                    onChange={(value) => handleVictimFormChange({ weight: value })}
                    name="weightRange"
                    required
                />
            </Field>        

            <Field label="Approximate Height" required>
                <ButtonGroup
                    options={PERSON_ENUMS.HEIGHT_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={victimForm.height}
                    onChange={(value) => handleVictimFormChange({ height: value })}
                    name="heightRange"
                    required
                />
            </Field>

            <Field label="Eye Color" required>
                <ButtonGroup
                    options={PERSON_ENUMS.EYE_COLORS.map(e => ({ label: e.label, value: e.value }))}
                    value={victimForm.eyes}
                    onChange={(value) => handleVictimFormChange({ eyes: value })}
                    name="eyes"
                    required
                />
            </Field>

            <Field label="Hair Color" required>
                <ButtonGroup
                    options={PERSON_ENUMS.HAIR_COLORS.map(e => ({ label: e.label, value: e.value }))}
                    value={victimForm.hair}
                    onChange={(value) => handleVictimFormChange({ hair: value })}
                    name="hair"
                    required
                />
            </Field>

            <Field label={<>Additional Features <span className='sublabel'>(you can select multiple options)</span></>}>
                <ButtonGroup
                    options={PERSON_ENUMS.FEATURES.map(e => ({ label: e.label, value: e.value }))}
                    value={victimForm.features}
                    onChange={(value) => handleVictimFormChange({ features: value })}
                    name="features"
                    multiple
                />
            </Field>

            {victimForm.features?.includes(TATTOO_LABEL.value) && (
                <Field label={<>Tattoo Images <span className='sublabel'>(you can upload up to 10)</span></>}>
                    <ImageUploader
                        files={victimForm.tattooImages}
                        onChange={(files) => {
                            if (files.length > 10) {
                                showToast('Maximum 10 images allowed for tattoos', 'error');
                                return;
                            }
                            setVictimForm(prev => ({ ...prev, tattooImages: files }));
                        }}
                        maxFiles={10}
                        onFileDelete={(file) => {
                            if (file.id && onFileDelete) {
                                onFileDelete(file.id);
                            }
                        }}
                    />
                </Field>
            )}

            {victimForm.features?.includes(SCARS_LABEL.value) && (
                <Field label={<>Scar Images <span className='sublabel'>(you can upload up to 10)</span></>}>
                    <ImageUploader
                        files={victimForm.scarImages}
                        onChange={(files) => {
                            if (files.length > 10) {
                                showToast('Maximum 10 images allowed for scars', 'error');
                                return;
                            }
                            setVictimForm(prev => ({ ...prev, scarImages: files }));
                        }}
                        maxFiles={10}
                        onFileDelete={(file) => {
                            if (file.id && onFileDelete) {
                                onFileDelete(file.id);
                            }
                        }}
                    />
                </Field>
            )}

            <Field label="Additional Information">
                <Textarea
                    value={victimForm.additionalInfo}
                    onChange={(e) => handleVictimFormChange({ additionalInfo: e.target.value })}
                />
            </Field>

            <Field label="Image">
                <ImageUploader
                    files={victimForm.personalImage ? [victimForm.personalImage] : []}
                    onChange={(files) => setVictimForm(prev => ({ ...prev, personalImage: files[0] }))}
                    maxFiles={1}
                    onFileDelete={(file) => {
                        if (file.id && onFileDelete) {
                            onFileDelete(file.id);
                        }
                    }}
                    capture="environment"
                />
            </Field>

            <Field label="License / ID">
                <ImageUploader
                    files={victimForm.licenseImage ? [victimForm.licenseImage] : []}
                    onChange={(files) => setVictimForm(prev => ({ ...prev, licenseImage: files[0] }))}
                    maxFiles={1}
                    onFileDelete={(file) => {
                        if (file.id && onFileDelete) {
                            onFileDelete(file.id);
                        }
                    }}
                />
            </Field>

            <Field label="Was Left at Location" required>
                <InfiniteDropdown
                    value={victimForm.locationId}
                    onChange={(_, data) => {
                        handleLocationChange(data.value);
                        handleVictimFormChange({ locationId: data.value });
                    }}
                    onFilterChange={setLocationFilter}
                    queryResult={locationsQuery}
                    itemToOption={(location) => ({
                        value: location.id ?? '',
                        text: location.name ?? ''
                    })}
                    placeholder="Select location"
                    selectedItem={selectedLocationData}
                />
            </Field>

            <Field label="Location Address">
                <Input
                    value={selectedLocationData?.address || ''}
                    readOnly
                />
            </Field>

            <div className="divider"></div>

            <Switch
                label="Share the Victim Information for Reunification Purposes"
                checked={!victimForm.restrictions}
                onChange={(e) => handleVictimFormChange({ restrictions: !e.currentTarget.checked })}
            />

            <div className="form-actions">
                <Button
                    type="submit"
                    appearance="primary"
                    size="large"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : submitButtonText}
                </Button>
                <Button appearance="secondary" size="large" onClick={() => navigate(ROUTES.VTRACKER.ROOT)}>
                    Cancel
                </Button>
            </div>
        </form>
    );
};

export default VictimForm;
